(() => {
  'use strict';

  // Rewrites the wording of Shopify's consent banner. The banner itself, its buttons and
  // the Customer Privacy API behind them are left exactly as they are — only the prose is
  // ours.
  //
  // On the substance this deliberately takes nothing away. Shopify's default text discloses
  // three things: that partners including Shopify are involved, that the purposes are
  // personalisation, advertising and analytics, and where the full policy is. All three are
  // still stated below, in the same breadth, because narrowing a consent notice to make it
  // friendlier is how a compliant banner quietly stops being one — the store may run pixels
  // or apps this file knows nothing about, and the broad wording is what covers them.
  //
  // What changes is the tone and one addition. It opens with what is true before any choice
  // is made — the shop works without any of this — and it states that consent can be
  // withdrawn at any time, which Art. 7(3) GDPR requires the visitor to be told and which
  // Shopify's default text only implies through the preferences button.

  // Split around the policy link so the anchor element itself is reused rather than rebuilt:
  // its href, id and target come from Shopify and should stay under Shopify's control.
  //
  // The German wording is written, not translated. It carries exactly the same disclosures
  // — partners including Shopify, personalisation, advertising, analytics, the policy link
  // and the right to withdraw — in the register a German shop actually uses, and names the
  // legal basis the way § 25 TDDDG expects a consent notice to read.
  const COPY = {
    en: {
      title: 'Cookies',
      before:
        'This shop works without cookies that track you. With your consent, we and our ' +
        'partners — including Shopify — also use them to personalise your experience, ' +
        'measure how the shop is used, and show you relevant ads. You can change or ' +
        'withdraw your choice at any time. Details in our ',
      after: '.',
    },
    de: {
      title: 'Cookies',
      before:
        'Dieser Shop funktioniert ohne Cookies, die Sie verfolgen. Mit Ihrer Einwilligung ' +
        'setzen wir und unsere Partner — darunter Shopify — sie zusätzlich ein, um Ihren ' +
        'Besuch zu personalisieren, die Nutzung des Shops zu messen und Ihnen passende ' +
        'Werbung zu zeigen. Sie können Ihre Wahl jederzeit ändern oder widerrufen. ' +
        'Einzelheiten in unserer ',
      after: '.',
    },
  };

  // The locale is on <html lang>, written from request.locale.iso_code.
  const lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
  const T = COPY[lang] || COPY.en;

  // Returns whether the copy is now in place. The distinction matters: Shopify inserts the
  // <section> first and fills it a tick later, so an earlier version of this reported success
  // as soon as the element existed, stopped watching, and left the default wording standing.
  function rewrite() {
    const banner = document.getElementById('shopify-pc__banner');
    if (!banner) return false;
    if (banner.dataset.meningeaCopy === 'done') return true;

    const title = banner.querySelector('#shopify-pc__banner__body-title');
    const para = banner.querySelector('.shopify-pc__banner__body p');
    const link = banner.querySelector('#shopify-pc__banner__body-policy-link');

    // If Shopify ever changes this markup, leave their text alone rather than emptying the
    // notice. A banner with no wording would be worse than one with wording we did not pick.
    if (!title || !para || !link) return false;

    title.textContent = T.title;
    para.textContent = '';
    para.append(document.createTextNode(T.before), link, document.createTextNode(T.after));

    banner.dataset.meningeaCopy = 'done';
    return true;
  }

  if (!rewrite()) {
    // The banner is injected by an async script, and it can be injected again later when the
    // visitor reopens preferences from the footer. Observing is more reliable than a timeout.
    const observer = new MutationObserver(() => {
      if (rewrite()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // The footer link that lets someone come back and change their mind — the practical half
  // of "you can withdraw at any time", and what the privacy policy promises is possible
  // "at any time".
  //
  // Two APIs are tried because Shopify ships both: window.privacyBanner is the current one,
  // Shopify.customerPrivacy the older one that is still what loads on some stores.
  //
  // preventDefault comes *after* establishing that one of them is actually there. The
  // earlier version called it first and then did nothing if neither API had loaded, which
  // turned the link into a dead control — a click that visibly does nothing is worse than
  // the fallback, and worse than useless when the policy text promises the choice can be
  // changed here. Without an API the href stands and the visitor lands on the policy, which
  // at least tells them what was collected and how to object.
  function preferencesOpener() {
    if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
      return () => window.privacyBanner.showPreferences();
    }
    const legacy = window.Shopify && window.Shopify.customerPrivacy;
    if (legacy && typeof legacy.showPreferences === 'function') {
      return () => legacy.showPreferences();
    }
    return null;
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cookie-preferences]');
    if (!trigger) return;
    const open = preferencesOpener();
    if (!open) return;
    event.preventDefault();
    open();
  });
})();
